import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, RecordData, Service, Slack } from 'webapp-types';
import type { ReportLayout } from 'webapp/enums/enums';
import { ModelName } from 'webapp/enums/enums';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Report from 'webapp/models-custom/report';
import type { ScheduleUser } from 'webapp/models-custom/schedule';
import Schedule from 'webapp/models-custom/schedule';
import type ReportScheduleSettingRecord from 'webapp/models/report-schedule-setting';
import { DEFAULT_NOTE } from 'webapp/models/report-schedule-setting';
import type UserRecord from 'webapp/models/user';

import { Severity } from 'webapp/services/logger';

export default class ReportScheduleSetting extends BufferedModel<
	ReportScheduleSettingRecord,
	RecordData.ReportScheduleSetting.Serialized
> {
	@service('timer') declare timerService: Service.Timer;
	@service('logger') declare loggerService: Service.Logger;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get reportId(): number {
		return this.getBuffered('reportId');
	}

	set reportId(value: number) {
		this.setBuffered('reportId', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get recipients(): string[] {
		return this.getBuffered('recipients');
	}

	set recipients(value: string[]) {
		this.setBuffered('recipients', value);
	}

	get slackChannels(): Slack.Channels {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channels) {
		this.setBuffered('slackChannels', value);
	}

	get subject(): string {
		return this.getBuffered('subject');
	}

	set subject(value: string) {
		this.setBuffered('subject', value);
	}

	get note(): string {
		return this.getBuffered('note');
	}

	set note(value: string) {
		this.setBuffered('note', value);
	}

	get password(): string {
		return this.getBuffered('password');
	}

	set password(value: string) {
		this.setBuffered('password', value);
	}

	get scheduledOn(): number {
		return this.getBuffered('scheduledOn');
	}

	set scheduledOn(value: number) {
		this.setBuffered('scheduledOn', value);
	}

	get scheduleType(): number {
		return this.getBuffered('scheduleType');
	}

	set scheduleType(value: number) {
		this.setBuffered('scheduleType', value);
		this.schedule.type = value;
	}

	get scheduleHourOfDay(): number | null {
		return this.getBuffered('scheduleHourOfDay');
	}

	set scheduleHourOfDay(value: number | null) {
		this.setBuffered('scheduleHourOfDay', value);
		this.schedule.hourOfDay = value;
	}

	get scheduleDayOfWeek(): number[] {
		return this.getBuffered('scheduleDayOfWeek');
	}

	set scheduleDayOfWeek(value: number[]) {
		this.setBuffered('scheduleDayOfWeek', value);
		this.schedule.dayOfWeek = value;
	}

	get scheduleDayOfMonth(): number | null {
		return this.getBuffered('scheduleDayOfMonth');
	}

	set scheduleDayOfMonth(value: number | null) {
		this.setBuffered('scheduleDayOfMonth', value);
		this.schedule.dayOfMonth = value;
	}

	get scheduleUser(): ScheduleUser | null {
		return this.getBuffered('scheduleUser');
	}

	set scheduleUser(value: ScheduleUser | null) {
		this.setBuffered('scheduleUser', value);
		this.schedule.user = value;
	}

	get includeBgImages(): boolean {
		return this.getBuffered('includeBgImages');
	}

	set includeBgImages(value: boolean) {
		this.setBuffered('includeBgImages', value);
	}

	get layout(): ReportLayout {
		return this.getBuffered('layout');
	}

	set layout(value: ReportLayout) {
		this.setBuffered('layout', value);
	}

	// get approvalRequired(): boolean {
	// 	return this.getBuffered('approvalRequired');
	// }

	// set approvalRequired(value: boolean) {
	// 	this.setBuffered('approvalRequired', value);
	// 	if (!value) {
	// 		// eslint-disable-next-line ember/no-assignment-of-untracked-properties-used-in-tracking-contexts
	// 		this.approvalIds = [];
	// 	}
	// }

	// get approvalIds(): number[] {
	// 	return this.getBuffered('approvalIds');
	// }

	// set approvalIds(value: number[]) {
	// 	this.setBuffered('approvalIds', value);
	// }

	// ------ schedule -------

	declare _schedule: Schedule;

	get schedule(): Schedule {
		if (this._schedule) {
			// mark dependents so this getter updates and triggers the schedule update from record
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleType;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleHourOfDay;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfWeek;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfMonth;
			once(this._schedule, this._schedule.updateScheduleFromModel, this);
		} else {
			this._schedule = Schedule.createFromModel(this);
		}
		return this._schedule;
	}

	updateScheduleValues(): void {
		const schedule = this.schedule;
		schedule.updateScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	cleanupUnusedScheduleValues(): void {
		const schedule = this.schedule;
		schedule.cleanupUnusedScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	getTrackingDataForCreate(origin: string) {
		const deliveryTextsForTracking = [];
		if (this.isEmailDelivery) {
			deliveryTextsForTracking.push('email');
		}
		if (this.isSlackDelivery) {
			deliveryTextsForTracking.push('slack');
		}

		const delivery = deliveryTextsForTracking.join(', ');

		if (this.schedule.isOneOff) {
			const isNow = !this.scheduledOn,
				eventProps: APIData.Tracking.Data = {
					when: isNow ? 'now' : 'later',
					delivery,
					origin,
				};
			if (!isNow) {
				eventProps.days_to_send = moment.unix(this.scheduledOn).diff(moment.now(), 'days');
			}

			return eventProps;
		} else {
			return {
				frequency: this.schedule.typeText.toLowerCase(),
				delivery,
				origin,
			};
		}
	}

	// ------- other props ------

	get isMine(): boolean {
		return this.createdBy === this.currentUserService.numericId;
	}

	get isOneOff(): boolean {
		return !!this.scheduledOn || this.schedule.isOneOff;
	}

	get isAutomated(): boolean {
		return !this.isOneOff;
	}

	get oneOffTimeLeft(): number {
		return this.scheduledOn - this.timerService.currentTs;
	}

	get canCurrentUserEdit(): boolean {
		return this.isMine || !!this.report?.canCurrentUserEdit;
	}

	get isNoteDefault() {
		return this.note === DEFAULT_NOTE;
	}

	@cached
	get report(): Report | null {
		let report;
		if (this.reportId) {
			report = this.mmReportsService.findModel(String(this.reportId));
			if (!report) {
				this.loggerService.logError(
					'Missing report for report schedule setting',
					{
						reportScheduleSettingId: this.id,
						reportId: this.reportId,
					},
					Severity.WARNING,
				);
			}
		}
		return report || null;
	}

	get target() {
		// for compatibility with Boards::Share::Schedule::IncludeBgImages model
		return this.report;
	}

	get createdByUser(): UserRecord | null {
		let user;
		if (this.createdBy) {
			user = this.mmUsersService.findRecord(String(this.createdBy));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for report-schedule-setting',
					{
						scheduleSettingId: this.id,
						userId: this.createdBy,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get createdByText(): string {
		return this.createdByUser?.name || '/';
	}

	get recipientsText(): string {
		return this.mappedRecipients.map((item) => item.name).join(', ');
	}

	get mappedRecipients() {
		return this.mmUsersService.getMappedRecipients(
			this.recipients,
			[],
			this.slackChannels,
			false,
		);
	}

	get isEmailDelivery(): boolean {
		return this.recipients.length > 0;
	}

	get isSlackDelivery(): boolean {
		return this.slackChannels.length > 0;
	}

	// get canAnyoneApprove(): boolean {
	// 	return this.approvalRequired
	// 		&& this.approvalIds.length === 0;
	// }

	// get needsMyApprovalToSend(): boolean {
	// 	return this.approvalRequired
	// 		&& (   this.canAnyoneApprove
	// 			|| this.approvalIds.includes(this.currentUserService.numericId as number)
	// 		);
	// }

	// get approvalIdsUsers(): UserRecord[] {
	// 	const users: UserRecord[] = [];
	// 	this.approvalIds.forEach((userId) => {
	// 		const user = this.mmUsersService.findRecord(String(userId));
	// 		if (user) {
	// 			users.push(user);
	// 		} else {
	// 			this.loggerService.logError('Missing approvalIds user for report schedule setting', {
	// 				reportScheduleSettingId: this.id,
	// 				userId
	// 			}, Severity.WARNING);
	// 		}
	// 	});
	// 	return users;
	// }

	getDataForClone() {
		const data = this.toJSON();

		delete data.created;
		delete data.createdBy;

		return data;
	}

	createAnotherModel() {
		return new ReportScheduleSetting({ model: this.model });
	}

	static override modelName = ModelName.REPORT_SCHEDULE_SETTING; // store record type
}
